import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejectedWithValue } from '@reduxjs/toolkit';
import { has } from 'lodash';
import moment from 'moment';
import { SHC_NODES, SHC_RESULT, SHC_RESULTS, SHC_RULE } from '../settings';
import { fetchData } from '../utils';
import { getUploadDetails } from './api/uploads';
import { setNotification } from './notificationsSlice';
import { schemas } from './schemas';

export const getShcNodes = createAsyncThunk(
    "getShcNodes",
    async (arg, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(SHC_NODES, {}, {});
            return response.result.nodes;
        } catch (e) {
            dispatch(setNotification(e));
            return rejectWithValue({ code: e.code })
        }
    }
)

export const getShcResults = createAsyncThunk(
    "getShcResults",
    async (node, { dispatch, getState, rejectWithValue }) => {
        try {
            const { dates } = getState().shc;
            const startDate = node ? dates[0] : moment().utc().subtract(1, "month").format("YYYY-MM-DD");
            const endDate = node ? dates[1] : moment().utc().subtract(1, "day").format("YYYY-MM-DD");
            let params = ["start_date=" + startDate, "end_date=" + endDate];
            if (node) params.push("node=" + node);

            const response = await fetchData(SHC_RESULTS + "?" + params.join("&"), {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            }, schemas.SHC_RESULTS);
            if (has(response.entities, "shcResults")) {
                return Object.values(response.entities.shcResults);
            };
            return []
        } catch (e) {
            dispatch(setNotification(e));
            return rejectWithValue({ code: e.code })
        }
    }
)

export const getShcResult = createAsyncThunk(
    "getShcResult",
    async (file, { dispatch, rejectWithValue }) => {
        try {
            const response = await fetchData(SHC_RESULT, {
                headers: { "X-Upload-Ref": file },
            }, schemas.SHC_RESULT);
            if (has(response.entities, "shcResult")) {
                dispatch(getUploadDetails(file));
                return response.entities.shcResult[file];
            };
            return []
        } catch (e) {
            dispatch(setNotification(e));
            return rejectWithValue({ code: e.code })
        }
    }
)

export const getShcRule = createAsyncThunk(
    "getShcRule",
    async (rule, { dispatch }) => {
        try {
            const response = await fetchData(SHC_RULE, {
                headers: { "X-Rule-Id": rule},
            }, {});

            return response.result;
        } catch (e) {
            dispatch(setNotification(e));
        }
    }
)

const initialState = {
    nodes: [],
    results: [],
    result: {},
    rule: {},
    interval: "month",
    dates: [moment().utc().subtract(1, "month").format("YYYY-MM-DD"), moment().utc().subtract(1, "day").format("YYYY-MM-DD")],
    onlyFailed: false,
    includingPassed: true,
    forbidden: false,
    notFound: false
};

const isAPendingAction = isPending(getShcNodes, getShcResults, getShcResult);
const isAFulfilledAction = isFulfilled(getShcNodes, getShcResults, getShcResult);
const isARejectedAction = isRejectedWithValue(getShcNodes, getShcResult);

const shc = createSlice({
    name: "shc",
    initialState,
    reducers: {
        showOnlyFailed: (state, action) => {
            state.onlyFailed = action.payload;
        },
        changeDates: (state, action) => {
            state.dates = action.payload.dates;
            state.interval = action.payload.interval;
        },
        includePassed: (state, action) => {
            state.includingPassed = action.payload;
        },
        clearRule: (state) => {
            state.rule = {};
        },
        resetData: () => {
            return initialState;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getShcNodes.fulfilled, (state, { payload }) => {
                state.nodes = payload;
            })
            .addCase(getShcResults.fulfilled, (state, { payload }) => {
                state.results = payload;
            })
            .addCase(getShcResults.rejected, (state, { payload }) => ({
                ...initialState,
                nodes: state.nodes,
                forbidden: payload.code === 403,
                notFound: payload.code === 404

            }))
            .addCase(getShcResult.fulfilled, (state, { payload }) => {
                state.result = payload;
            })
            .addCase(getShcRule.fulfilled, (state, { payload }) => {
                state.rule = payload;
            })
            .addMatcher(
                action => isAPendingAction(action),
                (state) => {
                    state.results = null;
                }
            )
            .addMatcher(
                action => isAFulfilledAction(action),
                (state, { payload }) => {
                    state.forbidden = payload.code === 403
                    state.notFound = payload.code === 404
                }
            )
            .addMatcher(
                action => isARejectedAction(action),
                (state, { payload }) => ({
                    ...initialState,
                    forbidden: payload.code === 403,
                    notFound: payload.code === 404
                })
            )
    }
});

export const { showOnlyFailed, changeDates, includePassed, clearRule, resetData } = shc.actions;
export default shc.reducer;